<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Payment:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Amount:</b-th>
                    <b-th>Type:</b-th>
                    <b-th>Date:</b-th>
                    <b-th>Applied to Invoice:</b-th>
                    <b-th>Balance Applied To:</b-th>
                    <b-th v-if="enableIntegratedPayments">Transaction ID:</b-th>
                    <b-th>Notes:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="payment in payments" :key="payment.id">
                    <b-td class="text-nowrap">
                        Payment #{{ payment.index }}
                    </b-td>
                    <b-td>
                        <span v-if="payment.client">
                            <client-name
                                :client="payment.client"
                                :text="payment.client.display_name"
                            />
                        </span>
                    </b-td>
                    <b-td>{{ _toCurrency(payment.totalAmount) }}</b-td>
                    <b-td>
                        {{
                            payment.payment_type
                                ? payment.payment_type.name
                                : null
                        }}
                    </b-td>
                    <b-td class="text-nowrap">{{
                        _carbonDateDisplay(payment.payment_date)
                    }}</b-td>
                    <b-td>
                        {{ _toCurrency(payment.applied_amount) }}
                    </b-td>
                    <b-td>
                        <div v-if="payment.balance_applied_to">
                            <div
                                v-for="inv in payment.balance_applied_to
                                    .invoices"
                                :key="inv.invoice_id"
                            >
                                <invoice-link :invoice="inv" :text="inv.name" />
                                <span> ({{ _toCurrency(inv.amount) }})</span>
                            </div>

                            <!-- not been tested yet -->
                            <div
                                v-for="refund in payment.balance_applied_to
                                    .refunds"
                                :key="refund.refund_id"
                            >
                                <strong
                                    class="text-primary cursor-pointer mr-1"
                                    @click="
                                        openRefundModal(refund, payment.client)
                                    "
                                    >{{ refund.name }}</strong
                                >
                                <span> ({{ _toCurrency(refund.amount) }})</span>
                            </div>

                            <!-- not been tested yet -->
                            <div
                                v-for="credit in payment.balance_applied_to
                                    .credits"
                                :key="credit.credit_id"
                            >
                                <strong
                                    class="text-primary cursor-pointer mr-1"
                                    @click="
                                        showEditCreditModal(
                                            payment.credit,
                                            payment.client
                                        )
                                    "
                                    :disabled="isHasUsersChooseInvoice"
                                    >{{ credit.name }}</strong
                                >
                                <span> ({{ _toCurrency(credit.amount) }})</span>
                            </div>

                            <div
                                v-for="fund in payment.balance_applied_to
                                    .charitable_funds"
                                :key="fund.fund_id"
                            >
                                <span>{{ fund.name }}</span>
                                <span> ({{ _toCurrency(fund.amount) }})</span>
                            </div>
                        </div>
                    </b-td>
                    <b-td v-if="enableIntegratedPayments">
                        <span
                            v-if="
                                payment.cc_transaction == 'STRIPE' ||
                                payment.cc_transaction == 'CCONNECT'
                            "
                        >
                            <transaction-id-link
                                :transactionId="payment.transaction_id"
                                :transactionType="payment.cc_transaction"
                                :account="paymentAccount"
                            />
                        </span>
                        <span v-else>
                            -
                        </span>
                        <!-- <span>{{ payment.transaction_id }}</span> -->
                    </b-td>
                    <b-td>{{ payment.reference }}</b-td>

                    <b-td
                        class="text-right"
                        v-if="
                            loadingTransactionId != payment.transaction_id &&
                            payment.status == 1
                        "
                    >
                        <!-- 6 is reopened -->
                        <spinner v-if="isProcessing" />
                        <b-dropdown
                            v-if="
                                isBusinessActorActive(payment.client) &&
                                invoice.status != 6 &&
                                !isProcessing &&
                                !hasRefunded(payment)
                            "
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <span v-if="payment.client">
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess(
                                            'billing.payment.receipt.send'
                                        )
                                    "
                                    @click="showEmailReceipt(payment)"
                                >
                                    <i class="cus-email"></i>
                                    Email Receipt
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    @click="printReceiptPdf(payment)"
                                >
                                    <i class="cus-printer"></i>
                                    Print Receipt
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        checkNotEmpty(payment.client.mobile) &&
                                        (_isExtensionEnabled('SMS') ||
                                            _isExtensionEnabled(
                                                '2_WAY_MESSAGING'
                                            )) &&
                                        _hasAccess(
                                            'billing.payment.receipt.send'
                                        )
                                    "
                                    @click="sendSMS(payment)"
                                >
                                    <i class="cus-email"></i>
                                    SMS (text) Receipt
                                </b-dropdown-item>
                            </span>
                            <b-dropdown-item
                                href="#"
                                v-if="
                                    _hasAccess('billing.payment.delete') &&
                                    !payment.is_return_posted &&
                                    payment.is_deletable
                                "
                                class="p-0"
                                :disabled="isHasUsersChooseInvoice"
                            >
                                <div
                                    v-if="payment.transaction_id"
                                    @click="showDeletePayment(payment)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Refund To Card
                                </div>
                                <div v-else @click="showDeletePayment(payment)">
                                    <i class="cus-delete"></i> Delete Payment
                                </div>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                    <b-td class="text-right" v-else-if="payment.status == 0">
                        <span
                            class="badge bg-secondary"
                            style="font-weight: bold;"
                            v-b-tooltip.hover.top
                            title="The customer's bank is processing this payment"
                            >Pending <i class="fas fa-clock"></i
                        ></span>
                    </b-td>
                    <b-td class="text-right" v-else>
                        <moon-loader color="black" size="16px" />
                    </b-td>
                </b-tr>
                <b-tr v-if="payments.length == 0 && !isLoading">
                    <b-td v-if="invoice.balance > 0" colspan="9"
                        ><i>No records available</i></b-td
                    >
                    <b-td v-else colspan="9">
                        <i>
                            Payments can only be applied against invoices with a
                            balance that is greater than zero.
                        </i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && showPagination"
            v-show="meta.lastPage > 1"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getPayments"
        />
        <modal-email-payment
            ref="modalEmailPayment"
            :payment="selectedPayment"
            :client="clientDetails"
            @reload="reloadRelated()"
        />
        <modal-delete-payment
            ref="modalDeletePayment"
            :payment="selectedPayment"
            :invoiceId="invoice.id"
            @reload="reloadRelated()"
            @loaded="updateGearIconStatus()"
        />
        <modal-edit-credit
            modal-id="modalEditCredit"
            ref="modalEditCredit"
            :client-name="selectedClient"
            @reload="reloadRelated(meta.currentPage)"
        />
        <refund-edit-modal
            modal-ref="refundEditModal"
            ref="refundEditModal"
            :refund="selectedRefund"
            :client-name="selectedClient"
            @reload="reloadRelated(meta.currentPage)"
        />
    </div>
</template>

<script>
/**
 * @name PaymentList
 * @description List of payment
 * @see apps/application/views/staff/billing/sub/payment/list.php
 * @see apps/application/models/orm/TblPayment.php
 */
import { isEmpty } from "lodash";
import Loader from "@/components/elements/loader-list";
import ClientName from "@/components/commons/client-name";
import TransactionIdLink from "@/components/commons/transaction-id-link";
import InvoicePaymentService from "@/services/invoice/invoice-payments";
import BillingPaymentServices from "@/services/billing/billing-payments";
import ModalEmailPayment from "@/components/payments/payment-email-modal";
import ModalEditCredit from "@/components/billing-credit/credit-edit-modal";
import RefundEditModal from "@/components/client-refunds/refund-edit";
import ModalDeletePayment from "@/components/payments/payment-delete-modal";
import Payment from "@/services/payment";
import MoonLoader from "vue-spinner/src/MoonLoader.vue";
import EXTENSIONS from "@/constants/extensions";

export default {
    mixins: {},
    components: {
        Loader,
        ClientName,
        TransactionIdLink,
        ModalEmailPayment,
        ModalEditCredit,
        RefundEditModal,
        ModalDeletePayment,
        InvoiceLink: () => import("@/components/commons/invoice-link"),
        Spinner: () => import("@/components/elements/spinner"),
        MoonLoader
    },
    props: {
        invoice: {
            type: Object,
            required: true
        }
    },
    computed: {
        isHasUsersChooseInvoice() {
            return !isEmpty(
                this.$store.getters["billing/findUserChooseInvoice"](
                    this.invoice.id
                )
            );
        }
    },
    data() {
        return {
            isLoading: false,
            isProcessing: false,
            showPagination: false,
            payments: [],
            selectedPayment: null,
            clientDetails: null,
            meta: null,
            selectedClient: null,
            selectedRefund: { index: null },
            loadingTransactionId: "",
            paymentAccount: null,
            enableIntegratedPayments: false
        };
    },
    mounted() {
        this.getPayments();
        this.getPaymentAccounts();
    },
    methods: {
        hasRefunded(rec) {
            return rec.applied_amount === 0 && rec.refund_id;
        },
        isBusinessActorActive(client) {
            return (
                client &&
                client.single_business_actor &&
                client.single_business_actor.isactive
            );
        },
        getPayments(page = 1) {
            let self = this;
            this.isLoading = true;
            const params = {
                perPage: this.PER_PAGE,
                page: page
            };
            InvoicePaymentService.getPayments(this.invoice.id, params).then(
                res => {
                    this.isLoading = false;
                    self.meta = res.meta;

                    if (self.meta.total > 20) {
                        self.showPagination = true;
                    }

                    self.payments = res.resources.map(payment => {
                        payment.totalAmount = payment.amount;
                        return payment;
                    });
                }
            );
        },
        showDeletePayment(payment) {
            let self = this;
            let date = self._fromDateCarbon(payment.payment_date).toFormat("X");
            self.selectedPayment = payment;
            self.loadingTransactionId = payment.transaction_id;

            self.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => {
                    if (self.selectedPayment.cc_transaction == "STRIPE") {
                        Payment.getPaymentIntentObject(
                            self.selectedPayment.transaction_id
                        )
                            .then(response => {
                                if (response.message.code == 200) {
                                    self.selectedPayment = _.merge(
                                        self.selectedPayment,
                                        {
                                            trans_type: response.message.type,
                                            charge: response.message.charge,
                                            payment_intent:
                                                response.message.payment_intent,
                                            terminal: null,
                                            terminals: []
                                        }
                                    );
                                    self.$refs["modalDeletePayment"].open();
                                } else {
                                    self._message(
                                        response.message.description,
                                        "danger"
                                    );
                                    self.loadingTransactionId = "";
                                }
                            })
                            .catch(error => {
                                console.error(error);
                            });
                    } else {
                        self.$refs["modalDeletePayment"].open();
                    }
                },
                onError: message => self._message(message, "danger")
            });
        },
        prepareData(data) {
            data.client = {
                display_name: data.client_name,
                name: data.client_name,
                encoded_id: data.client_encoded_id,
                id: data.client_id
            };
            return data;
        },
        reloadRelated(page) {
            this.getPayments(page);
            this.$emit("reload-invoice-details");
        },
        checkNotEmpty(value) {
            return !_.isEmpty(value);
        },
        showEmailReceipt(payment) {
            this.selectedPayment = payment;
            this.selectedPayment.name = `Payment #${payment.index}`;
            this.clientDetails = payment.client;
            this.$refs["modalEmailPayment"].open();
        },
        printReceiptPdf(payment) {
            let self = this;
            this.isProcessing = true;
            BillingPaymentServices.printReceipt(payment.id)
                .then(response => {
                    if (response.code == 200) {
                        this._message("Opening PDF");
                        if (response.resource) {
                            setTimeout(function () {
                                window.open(response.resource);
                            }, 1000);
                        }
                    } else {
                        this._message(response.message, "danger");
                    }
                })
                .catch(error => {
                    this._message("Error : Unable to print receipt", "danger");
                    console.error(error);
                })
                .finally(() => (self.isProcessing = false));
        },
        sendSMS(payment) {
            let data = { payment_id: payment.id };
            let self = this;
            this.isProcessing = true;
            BillingPaymentServices.sendSMS(data)
                .then(response => {
                    if (response.code == 200) {
                        this._message(response.message);
                    } else {
                        this._message(response.message, "danger");
                    }
                })
                .catch(error => {
                    this._message("Error : Unable to send sms", "danger");
                    console.error(error);
                })
                .finally(() => (self.isProcessing = false));
        },
        openRefundModal(refund, client) {
            this.selectedRefund = Object.assign(
                { isHasReeturn: refund.return_encoded_id !== false },
                refund
            );
            this.selectedClient = client.label;
            this.$store.dispatch("checkBillingLock", {
                date: this.selectedRefund.refund_date
                    ? this._fromDateCarbon(
                          this.selectedRefund.refund_date
                      ).toFormat("X")
                    : null,
                onProceed: () => this.$refs["refundEditModal"].open(),
                onError: message => this._message(message, "danger")
            });
        },
        async showEditCreditModal(credit, client) {
            this.selectedClient = client.label;
            let date = credit.credit_date
                ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                : null;

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs["modalEditCredit"].open(credit),
                onError: message => this._message(message, "danger")
            });
        },
        updateGearIconStatus() {
            this.loadingTransactionId = "";
        },
        getPaymentAccounts() {
            let self = this;

            let { CODE } = EXTENSIONS;
            self.enableIntegratedPayments =
                self._isExtensionEnabled(CODE.CODE_CCONNECT) ||
                self._isExtensionEnabled(CODE.CODE_GRAVITY) ||
                self._isExtensionEnabled(CODE.CODE_STRIPE);

            Payment.getPaymentAccounts(this._setting.business.id)
                .then(response => {
                    if (response.message.code == 200) {
                        self.paymentAccount =
                            response.message.payment_processor_id;
                    } else {
                        self._message(response.message.description, "danger");
                        self.paymentAccount = null;
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        }
    }
};
</script>
